import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import LoadingCenter from '../util/LoadingCenter';
import EmptyState from '../util/EmptyState';

interface Props { access: any; }

const asArray = (d: any) => (Array.isArray(d) ? d : d?.data ?? []);
const PRIORITY: Record<string, { cls: string; key: string }> = {
  high: { cls: 'rejected', key: 'High' },
  med: { cls: 'pending', key: 'Medium' },
  low: { cls: 'new', key: 'Low' },
};

const MobileTasks: React.FC<Props> = () => {
  const { t } = useTranslation();
  const auth = useSelector((state: RootState) => state.auth.value);
  const [tasks, setTasks] = useState<any[]>([]);
  const [meId, setMeId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'my' | 'done'>('all');
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, tk] = await Promise.allSettled([axios.get('user'), axios.get('tasks')]);
      if (u.status === 'fulfilled') setMeId(u.value.data?.id ?? null);
      if (tk.status === 'fulfilled') setTasks(asArray(tk.value.data));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { if (auth) load(); }, [auth, load]);

  const toggle = async (task: any) => {
    const next = task.status === 'fulfilled' ? 'new' : 'fulfilled';
    setBusyId(task.id);
    setTasks((prev) => prev.map((x) => (x.id === task.id ? { ...x, status: next } : x)));
    try {
      await axios.put(`tasks/${task.id}`, { status: next });
    } catch {
      setTasks((prev) => prev.map((x) => (x.id === task.id ? { ...x, status: task.status } : x)));
    } finally {
      setBusyId(null);
    }
  };

  const filtered = useMemo(() => {
    if (filter === 'done') return tasks.filter((t) => t.status === 'fulfilled');
    if (filter === 'my' && meId != null) return tasks.filter((t) => (t.assignees || []).some((a: any) => a.id === meId));
    return tasks;
  }, [tasks, filter, meId]);

  if (loading) return <LoadingCenter />;

  const myCount = meId != null ? tasks.filter((t) => (t.assignees || []).some((a: any) => a.id === meId)).length : 0;
  const doneCount = tasks.filter((t) => t.status === 'fulfilled').length;

  return (
    <div className="flex flex-col gap-[10px]">
      <div className="m-chips">
        <button type="button" className={`m-chip${filter === 'all' ? ' on' : ''}`} onClick={() => setFilter('all')}>{t('All')} · {tasks.length}</button>
        <button type="button" className={`m-chip${filter === 'my' ? ' on' : ''}`} onClick={() => setFilter('my')}>{t('My')} · {myCount}</button>
        <button type="button" className={`m-chip${filter === 'done' ? ' on' : ''}`} onClick={() => setFilter('done')}>{t('Done')} · {doneCount}</button>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon="✅" message={t('NoTasks') as string} />
      ) : (
        <div className="m-list">
          {filtered.map((task) => {
            const done = task.status === 'fulfilled';
            const pr = PRIORITY[task.priority] || PRIORITY.med;
            return (
              <div key={task.id} className="m-li">
                <button type="button" className={`m-check${done ? ' done' : ''}`} disabled={busyId === task.id} onClick={() => toggle(task)} aria-label="toggle">
                  {done && <FontAwesomeIcon icon={faCheck} />}
                </button>
                <span className="m-li-main">
                  <span className="m-li-title" style={done ? { textDecoration: 'line-through', color: 'var(--text-color-calendar-week)' } : undefined}>{task.title || '—'}</span>
                  {task.dueDate && <span className="m-li-sub">{t('Due')} {moment(task.dueDate).format('D MMM')}</span>}
                </span>
                {!done && <span className={`sbadge ${pr.cls}`}>{t(pr.key)}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MobileTasks;
